import { useEffect, useRef } from 'react'

import { GifIcon, KameraIcon, PhotoIcon } from '../../components/Icons'

/**
 * Was an eine Nachricht kann — hinter **einem** Knopf.
 *
 * Aus dem Betrieb: „am Smartphone ist das Eingabefeld zu schmal: die Symbole
 * für Fotoupload und GIF enger zusammen oder untereinander oder durch ein +
 * ersetzen, und dann ein Auswahlfenster mit Foto/Video / Kamera / GIF."
 *
 * **Der Platz ist der Punkt.** Jeder Knopf in der Eingabezeile ist 44 Punkte
 * breit, weil er es sein muss; auf 390 Punkten bleibt für den Text, was die
 * Knöpfe übrig lassen. Zwei davon in einen zu legen gibt dem Feld 52 Punkte
 * zurück — und im selben Zug kommt die Kamera dazu, die vorher gar keinen Weg
 * hatte.
 *
 * **Es ist eine Liste und keine Reihe.** Eine Reihe aus 44-Punkt-Flächen ist
 * nach acht Stück breiter als ein iPhone; untereinander kann sie nicht
 * hinausragen, und jede Zeile trägt ihren Namen statt eines Symbols, das man
 * erst lernen müsste.
 *
 * **Das Blatt löst nichts selbst aus.** Es meldet, was gewählt wurde; die
 * Dateifelder liegen in der Eingabezeile. Das ist keine Förmlichkeit: auf iOS
 * öffnet ein Dateifeld nur, wenn der Griff aus einer echten Geste kommt, und
 * ein Tipp auf eine Zeile ist genau das — ein Umweg über einen Zustand wäre es
 * nicht mehr.
 */
export function Anhangwahl({
  darfDateien,
  gifs,
  kamera,
  onFotoOderVideo,
  onKamera,
  onGif,
  onSchliessen,
}: {
  /** Fotos und Videos gehören dem Reisenden — bei allen anderen fehlt die Zeile. */
  darfDateien: boolean
  /** Ohne Schlüssel gibt es keine GIF-Suche und deshalb auch keine Zeile. */
  gifs: boolean
  /**
   * Ob die Kamera einen eigenen Weg verdient.
   *
   * Nur mit Finger: `capture` öffnet auf einem Telefon die Kamera, an einem
   * Rechner tut es nichts und der Eintrag führte zum selben Dateidialog wie
   * die Zeile darüber. Zwei Zeilen, die dasselbe tun, sind eine zu viel.
   */
  kamera: boolean
  onFotoOderVideo: () => void
  onKamera: () => void
  onGif: () => void
  onSchliessen: () => void
}) {
  const kasten = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function beiTaste(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onSchliessen()
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [onSchliessen])

  // **Ein Tipp daneben schließt.** Ohne das bliebe das Blatt stehen, bis
  // jemand es ausdrücklich zumacht -- und es deckt die halbe Eingabezeile.
  useEffect(() => {
    function beiTipp(event: PointerEvent) {
      const ziel = event.target
      if (ziel instanceof Node && kasten.current?.contains(ziel) === true) return
      onSchliessen()
    }
    // In der Aufnahmephase: ein Knopf darunter darf seine Sache trotzdem tun,
    // aber das Blatt schliesst dabei.
    window.addEventListener('pointerdown', beiTipp)
    return () => window.removeEventListener('pointerdown', beiTipp)
  }, [onSchliessen])

  return (
    <div
      ref={kasten}
      className="anhangwahl"
      role="dialog"
      aria-modal="true"
      aria-label="Was anhängen?"
    >
      {darfDateien && (
        <button type="button" className="anhangwahl__zeile" onClick={onFotoOderVideo}>
          <PhotoIcon size={20} />
          <span>Foto oder Video</span>
        </button>
      )}
      {darfDateien && kamera && (
        <button type="button" className="anhangwahl__zeile" onClick={onKamera}>
          <KameraIcon size={20} />
          <span>Kamera</span>
        </button>
      )}
      {gifs && (
        <button type="button" className="anhangwahl__zeile" onClick={onGif}>
          <GifIcon size={20} />
          {/* „GIF suchen" und nicht „GIF": das Sinnbild trägt das Wort schon,
              und zweimal dasselbe nebeneinander liest sich als Fehler. Der
              Zusatz sagt außerdem, was passiert -- es öffnet eine Suche. */}
          <span>GIF suchen</span>
        </button>
      )}
    </div>
  )
}

/**
 * Wie viele Wege es gibt — und damit, ob sich ein Sammelknopf lohnt.
 *
 * **Bei einem einzigen Weg gibt es keinen `+`.** Ein Knopf, der ein Blatt mit
 * genau einer Zeile öffnet, ist ein Griff mehr für nichts; dann steht der eine
 * Knopf wieder direkt da. Betrifft die Mitlesenden: sie dürfen GIFs schicken
 * und sonst nichts.
 */
export function wegeZaehlen(darfDateien: boolean, gifs: boolean, kamera: boolean): number {
  return (darfDateien ? 1 : 0) + (darfDateien && kamera ? 1 : 0) + (gifs ? 1 : 0)
}
